import React from 'react';
import styled from "@emotion/styled";
import { XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, Area, CartesianGrid } from 'recharts';

const formatCurrency = (value) => (
  value.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})
)

const roundToWholeThousand = (value) => Math.round(value / 1000) * 1000

const buildAxisTicks = (data, getValue, multiplier) => {
  const values = Array.isArray(data) ? data.map((item) => Number(getValue(item)) || 0) : []
  const maxValue = values.length ? Math.max(...values) : 0
  const visualMax = maxValue > 0 ? maxValue * multiplier : 3000
  const step = Math.max(1000, Math.ceil(visualMax / 3 / 1000) * 1000)

  return [0, step, step * 2, step * 3]
}

const CustomTooltip = ({ active, payload, teamMortgagesNum }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload

    return (
      <TooltipSurface>
        <TooltipHeader>
          <TooltipKicker>Portfolio Snapshot</TooltipKicker>
          <TooltipTitle>{data.sessionLabelFull}</TooltipTitle>
        </TooltipHeader>
        <TooltipRows>
          <TooltipRow>
            <TooltipLabel>Mortgages with financial information</TooltipLabel>
            <TooltipValue>
              {data.numberOfMortgages}/{teamMortgagesNum}
              <span>{data.numberOfMortgages/teamMortgagesNum*100}%</span>
            </TooltipValue>
          </TooltipRow>

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Total original loan amount</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalOriginalLoanAmount)}</TooltipValue>
          </TooltipRow>
          {((data.totalOriginalLoanAmount && data.totalOriginalLoanAmount > 0) && (data.numberOfMortgages && data.numberOfMortgages > 0)) &&
            <TooltipRow>
              <TooltipLabel>Average original loan amount</TooltipLabel>
              <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalOriginalLoanAmount/data.numberOfMortgages)}</TooltipValue>
            </TooltipRow>
          }
          <TooltipRow className="strong">
            <TooltipLabel>Outstanding principal</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalPrincipalRemaining)}</TooltipValue>
          </TooltipRow>
          {(data.totalPrincipalRemaining && (data.numberOfMortgages && data.numberOfMortgages > 0)) &&
            <TooltipRow>
              <TooltipLabel>Average outstanding principal</TooltipLabel>
              <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalPrincipalRemaining/data.numberOfMortgages)}</TooltipValue>
            </TooltipRow>
          }
          {(data.totalPrincipalRemaining && (data.totalOriginalLoanAmount && data.totalOriginalLoanAmount > 0)) &&
            <TooltipRow>
              <TooltipLabel>Percent of total remaining</TooltipLabel>
              <TooltipValue>{((data.totalPrincipalRemaining/data.totalOriginalLoanAmount)*100).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}%</TooltipValue>
            </TooltipRow>
          }

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Total original interest due</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalOriginalInterest)}</TooltipValue>
          </TooltipRow>
          {((data.totalOriginalInterest && data.totalOriginalInterest > 0) && (data.numberOfMortgages && data.numberOfMortgages > 0)) &&
            <TooltipRow>
              <TooltipLabel>Average original interest due</TooltipLabel>
              <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalOriginalInterest/data.numberOfMortgages)}</TooltipValue>
            </TooltipRow>
          }
          <TooltipRow className="strong">
            <TooltipLabel>Outstanding interest</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalInterestRemaining)}</TooltipValue>
          </TooltipRow>
          {(data.totalInterestRemaining && (data.numberOfMortgages && data.numberOfMortgages > 0)) &&
            <TooltipRow>
              <TooltipLabel>Average outstanding interest</TooltipLabel>
              <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalInterestRemaining/data.numberOfMortgages)}</TooltipValue>
            </TooltipRow>
          }
          {(data.totalInterestRemaining && (data.totalOriginalInterest && data.totalOriginalInterest > 0)) &&
            <TooltipRow>
              <TooltipLabel>Percent of total remaining</TooltipLabel>
              <TooltipValue>{((data.totalInterestRemaining/data.totalOriginalInterest)*100).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}%</TooltipValue>
            </TooltipRow>
          }

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Total payments received</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalPaymentsReceived)}</TooltipValue>
          </TooltipRow>
          <TooltipRow className="strong">
            <TooltipLabel>Total interest paid</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalInterestReceived)}</TooltipValue>
          </TooltipRow>
          <TooltipRow className="strong">
            <TooltipLabel>Total early payments</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalEarlyPayments)}</TooltipValue>
          </TooltipRow>

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Total assessed property value</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalAssessedPropertyValue)}</TooltipValue>
          </TooltipRow>
          {(data.totalAssessedPropertyValue && (data.numberOfMortgages && data.numberOfMortgages > 0)) &&
            <TooltipRow>
              <TooltipLabel>Average assessed property value</TooltipLabel>
              <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalAssessedPropertyValue/data.numberOfMortgages)}</TooltipValue>
            </TooltipRow>
          }
        </TooltipRows>
      </TooltipSurface>
    )
  }

  return null
}

const PortfolioFinancialsChart = ({ teamPortfolioStats, teamMortgagesNum }) => {
  const paymentsReceivedTicks = buildAxisTicks(
    teamPortfolioStats,
    (item) => item.totalPaymentsReceived,
    1.1
  )
  const outstandingBalanceTicks = buildAxisTicks(
    teamPortfolioStats,
    (item) => item.totalPrincipalRemaining + item.totalInterestRemaining,
    2.05
  )

  const renderTooltip = (props) => (
    <CustomTooltip
      {...props}
      teamMortgagesNum={teamMortgagesNum}
    />
  );

  return (
    <ChartBounds>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={teamPortfolioStats}
          layout="horizontal"
          margin={{
            top: 12,
            right: 30,
            left: 20,
            bottom: 4,
          }}
        >
          <CartesianGrid stroke="#edf2f7" vertical={false} />
          <XAxis dataKey="sessionLabel" tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <YAxis 
            type="number" 
            yAxisId="right" 
            stroke="#64748b" 
            orientation="right" 
            width={84}
            label={{ value: 'Total Payments Received', angle: 90, fontWeight: '800', fill: '#475569', dy: -72, dx: 54}} 
            ticks={paymentsReceivedTicks}
            domain={[0, paymentsReceivedTicks[3]]}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            tickFormatter={tick => {
              return `$${roundToWholeThousand(tick).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}`
            }}
          />
          <YAxis 
            type="number"
            yAxisId="left"
            stroke="#64748b"
            orientation="left"
            width={88}
            label={{ value: 'Outstanding Balance', angle: -90, fontWeight: '800', fill: '#475569', dy: -72, dx: -52 }}
            ticks={outstandingBalanceTicks}
            domain={[0, outstandingBalanceTicks[3]]}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            tickFormatter={tick => {
              return `$${roundToWholeThousand(tick).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}`
            }}
          />
          <Tooltip content={renderTooltip} cursor={{fill: `rgba(147, 149, 153, 0.12)`}} />
          <Legend wrapperStyle={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", paddingTop: "0.35rem" }} />
          <defs>
            <linearGradient id="portfolioColorPrincipal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5f78bd" stopOpacity={0.82}/>
              <stop offset="44%" stopColor="#6E88CC" stopOpacity={0.3}/>
              <stop offset="96%" stopColor="#6E88CC" stopOpacity={0.06}/>
            </linearGradient>
            <linearGradient id="portfolioColorInterest" x1="0" y1="0" x2="0" y2="1">
              <stop offset="4%" stopColor="#16803f" stopOpacity={0.74}/>
              <stop offset="36%" stopColor="#16803f" stopOpacity={0.26}/>
              <stop offset="58%" stopColor="#16803f" stopOpacity={0.1}/>
              <stop offset="96%" stopColor="#16803f" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area
            fillOpacity={0.3}
            type="monotone"
            yAxisId="left"
            dataKey="totalPrincipalRemaining"
            stackId="a"
            stroke="#526fb4"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#portfolioColorPrincipal)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#526fb4" }}
            name="Outstanding Principal"
          />
          <Area
            type="monotone"
            yAxisId="left"
            dataKey="totalInterestRemaining"
            stackId="a"
            stroke="#16803f"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#portfolioColorInterest)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#16803f" }}
            name="Outstanding Interest"
          />
          <Line
            strokeWidth={1.8}
            yAxisId="right"
            type="monotone"
            dataKey={"totalPaymentsReceived"}
            stroke="#1f6f3a"
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            activeDot={{ r: 4.5, stroke: "#ffffff", strokeWidth: 2, fill: "#1f6f3a" }}
            connectNulls
            name="Monthly Payments Received"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartBounds>
  )
}

const ChartBounds = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`

const TooltipSurface = styled.div(({ theme }) => `
  width: 24rem;
  max-height: 32rem;
  overflow: auto;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.85rem 1.9rem rgba(15, 23, 42, 0.16);
`)

const TooltipHeader = styled.div(({ theme }) => `
  padding: 0.78rem 0.9rem 0.68rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const TooltipKicker = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const TooltipTitle = styled.div(({ theme }) => `
  margin-top: 0.15rem;
  color: ${theme.palette.common.black};
  font-size: 1.04rem;
  font-weight: 900;
  line-height: 1.25;
`)

const TooltipRows = styled.div`
  display: grid;
  gap: 0.04rem;
  padding: 0.5rem 0.9rem 0.72rem;
`

const TooltipRow = styled.div(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.82rem;
  align-items: baseline;
  padding: 0.36rem 0;

  &.strong span:last-of-type {
    font-weight: 950;
  }
`)

const TooltipLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
`)

const TooltipValue = styled.span(({ theme }) => `
  display: inline-flex;
  align-items: baseline;
  gap: 0.12rem;
  color: ${theme.palette.common.black};
  font-size: 0.9rem;
  font-weight: 850;
  line-height: 1.2;
  text-align: right;
  
  span {
    font-size: 0.72rem;
    font-weight: 500;
    margin-left: 0.2rem;
  }
`)

const TooltipDivider = styled.div(({ theme }) => `
  height: 0.1rem;
  margin: 0.2rem 0;
  background: ${theme.palette.common.grayBorderLight};
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 0.76rem;
  font-weight: 900;
`)

export default PortfolioFinancialsChart
